import React, {memo, useCallback, useMemo, useState} from "react"
import {PasswordContainer} from "../../../../components/PasswordContainer"
import {SettingsPageContainer} from "../../../../components/Settings/SettingsPageContainer"
import {useLocalization} from "../../../../hooks/localization"
import {useKeyboard} from "../../../../hooks/keyboard"
import {useParams} from "react-router-dom"
import {useMessage} from "../../../../hooks/message"
import {useRPC} from "../../../../hooks/rpc"
import {SettingInputComponent} from "../../../../components/Settings/SettingInputComponent"
import {SettingReadOnlyRowComponent} from "../../../../components/Settings/SettingReadOnlyRowComponent"
import {useSetSelector} from "../../../../hooks/set-selector"
import {useDispatch, useSelector} from "react-redux"
import {MappingLogPopupScreen} from "../../Gateway/Setup/SetupMatchScreen"
import {setDetailedRFTest} from "../../../../store/control/actions"

export const DetailedRfTest = memo(function DetailedRfTest() {
    const params = useParams<{id: string; gatewayId: string}>()
    const BACK_URL = `/settings/device/device-settings/${params.id}`
    const {t} = useLocalization()
    const [setActiveNumpadInput, renderNumpadKeyboard, currentNumpadInput] = useKeyboard("numpad", true)
    const [setActiveNumpadExtendedInput, renderNumpadExtendedKeyboard, currentNumpadExtendedInput] = useKeyboard(
        "numpad-extended",
        true
    )
    const detailedRFTestResults = useSelector(state => state.control.detailedRFTestResponse)

    const bridgeDeviceSet = useSetSelector(state => state.control.bridgeDeviceList)
    const gatewaySet = useSetSelector(state => state.control.gatewayList)
    const defaultGateway = useMemo(() => gatewaySet[params?.gatewayId], [params, gatewaySet])
    const selectedDevice = useMemo(() => bridgeDeviceSet[params?.id], [bridgeDeviceSet, params])
    const [openMappingLog, setOpenMappingLog] = useState(false)
    const dispatch = useDispatch()

    const {failure} = useMessage()
    const [rpc] = useRPC()

    const [packageCount, setPackageCount] = useState("100")
    const [repeatCount, setRepeatCount] = useState("0")
    const [periodCount, setPeriodCount] = useState("100")
    const [cancellationErrorCount, setCancellationErrorCount] = useState("0")

    const onBlur = useCallback(() => {
        setActiveNumpadInput(null)
        setActiveNumpadExtendedInput(null)
    }, [setActiveNumpadInput, setActiveNumpadExtendedInput])

    const validationForDetailedRfTest = useCallback(() => {
        if (selectedDevice == null || defaultGateway == null) {
            return
        }
        if (isNaN(Number(packageCount)) || packageCount.length === 0) {
            failure("pls_enter_a_number")
            return
        }
        if (isNaN(Number(repeatCount)) || repeatCount.length === 0) {
            failure("pls_enter_a_number")
            return
        }
        if (isNaN(Number(periodCount)) || periodCount.length === 0) {
            failure("pls_enter_a_number")
            return
        }
        if (isNaN(Number(cancellationErrorCount)) || cancellationErrorCount.length === 0) {
            failure("pls_enter_a_number")
            return
        }
        return true
    }, [failure, selectedDevice, defaultGateway, cancellationErrorCount, packageCount, periodCount, repeatCount])

    const onClickStartTest = useCallback(() => {
        if (!validationForDetailedRfTest()) {
            return
        }

        return rpc
            .setDetailRfTest({
                device_rf_address: selectedDevice?.rf_address?.toString(),
                related_gateway: defaultGateway?.id,
                number_of_packet: packageCount,
                repeat: repeatCount,
                period: periodCount,
                halt_on_error: cancellationErrorCount
            }).then(() => setOpenMappingLog(true))
            .catch(failure)
    }, [
        validationForDetailedRfTest,
        failure,
        rpc,
        selectedDevice,
        defaultGateway,
        repeatCount,
        packageCount,
        periodCount,
        cancellationErrorCount
    ])

    const onClickClose = useCallback(() => {
        setOpenMappingLog(false)
        dispatch(setDetailedRFTest(null))
    }, [dispatch])

    const buttons = useMemo(
        () => [
            {
                text: t("start"),
                className: "green",
                onClick: onClickStartTest
            }
        ],
        [t, onClickStartTest]
    )

    return (
        <PasswordContainer requiredRole="service" isFullscreen={true}>
            <SettingsPageContainer
                className="service-edit"
                buttons={buttons}
                backUrl={BACK_URL}
                currentNumpadInput={currentNumpadInput}
                currentNumpadExtendedInput={currentNumpadExtendedInput}
            >
                {renderIf(openMappingLog)(() => (
                    <MappingLogPopupScreen
                        logs={
                            detailedRFTestResults != null
                                ? Object?.entries(detailedRFTestResults)?.map(([key, value]) => `${key}: ${value}`)
                                : []
                        }
                        isDone={false}
                        onClickClose={onClickClose}
                    />
                ))}

                <div className="content-divider">
                    <div className="content-wrapper">
                        <SettingReadOnlyRowComponent
                            title={t("rf_address")}
                            value={selectedDevice?.rf_address.toString()}
                        />
                        <SettingReadOnlyRowComponent title={t("gateway")} value={defaultGateway?.name} />
                        <SettingInputComponent
                            placeholder={t("pls_enter_package_number")}
                            label={t("package_number")}
                            value={packageCount}
                            setValue={setPackageCount}
                            setActiveInput={setActiveNumpadInput}
                            onBlur={onBlur}
                        />
                        <SettingInputComponent
                            placeholder={t("pls_enter_repeat_count")}
                            label={t("repeat_count")}
                            value={repeatCount}
                            setValue={setRepeatCount}
                            setActiveInput={setActiveNumpadInput}
                            onBlur={onBlur}
                        />
                        <SettingInputComponent
                            placeholder={t("pls_enter_period_count")}
                            label={t("period_count")}
                            value={periodCount}
                            setValue={setPeriodCount}
                            setActiveInput={setActiveNumpadInput}
                            onBlur={onBlur}
                        />
                        <SettingInputComponent
                            placeholder={t("pls_enter_cancellation_error_count")}
                            label={t("cancellation_error_count")}
                            value={cancellationErrorCount}
                            setValue={setCancellationErrorCount}
                            setActiveInput={setActiveNumpadInput}
                            onBlur={onBlur}
                        />
                    </div>
                    <div className="content-wrapper"></div>
                </div>
            </SettingsPageContainer>
            {renderNumpadKeyboard()}
            {renderNumpadExtendedKeyboard()}
        </PasswordContainer>
    )
})
